/**
 * RecoveryInstructionsModal - Displays the account recovery setup instructions in Japanese.
 * Opened from the Security tab via "アカウント回復の設定方法はこちら" link.
 * Uses same markdown-like rendering as DekInstructionsModal (##, ---, **bold**, tables).
 */

import React from 'react';
import { createPortal } from 'react-dom';
import { RECOVERY_INSTRUCTIONS_JA } from '../content/recoveryInstructionsJa';
import './DekInstructionsModal.css';

interface RecoveryInstructionsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function renderBold(text: string): React.ReactNode {
  const parts = text.split(/\*\*(.*?)\*\*/g);
  const result: React.ReactNode[] = [];
  parts.forEach((part, idx) => {
    if (idx % 2 === 1) result.push(<strong key={idx}>{part}</strong>);
    else result.push(part);
  });
  return <>{result}</>;
}

function renderInstructions(text: string): React.ReactNode {
  const lines = text.trim().split('\n');
  const nodes: React.ReactNode[] = [];
  let i = 0;
  let tableRows: string[] = [];
  const flushTable = () => {
    if (tableRows.length > 0) {
      const isSeparator = (r: string) => /^\|[\s\-:|]+\|$/.test(r.trim());
      const dataRows = tableRows.filter((r) => r.trim() && !isSeparator(r));
      const header = dataRows[0];
      const body = dataRows.slice(1);
      nodes.push(
        <div key={`table-${i}`} className="dek-instructions-table-wrap">
          <table className="dek-instructions-table">
            <thead>
              <tr>
                {header.split('|').filter((c) => c.trim()).map((cell, j) => (
                  <th key={j}>{renderBold(cell.trim())}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {body.map((row, ri) => (
                <tr key={ri}>
                  {row.split('|').filter((c) => c.trim()).map((cell, j) => (
                    <td key={j}>{renderBold(cell.trim())}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      tableRows = [];
    }
  };

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();
    if (trimmed.startsWith('## ')) {
      flushTable();
      nodes.push(<h3 key={i} className="dek-instructions-h3">{renderBold(trimmed.slice(3))}</h3>);
    } else if (trimmed === '---') {
      flushTable();
      nodes.push(<hr key={i} className="dek-instructions-hr" />);
    } else if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      tableRows.push(trimmed);
    } else if (tableRows.length > 0 && !trimmed) {
      flushTable();
    } else if (trimmed) {
      flushTable();
      nodes.push(<p key={i} className="dek-instructions-p">{renderBold(trimmed)}</p>);
    } else {
      flushTable();
      nodes.push(<br key={i} />);
    }
    i++;
  }
  flushTable();
  return <>{nodes}</>;
}

export const RecoveryInstructionsModal: React.FC<RecoveryInstructionsModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return createPortal(
    <div
      className="dek-instructions-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="recovery-instructions-title"
    >
      <div className="dek-instructions-modal">
        <div className="dek-instructions-header">
          <h2 id="recovery-instructions-title">アカウント回復の設定方法</h2>
          <button type="button" className="dek-instructions-close" onClick={onClose} aria-label="閉じる">
            ✕
          </button>
        </div>
        <div className="dek-instructions-body">
          {renderInstructions(RECOVERY_INSTRUCTIONS_JA)}
        </div>
      </div>
    </div>,
    document.body
  );
};
